import React, { useState } from 'react';
import { Container, Paper, TextField, Button, Typography, Box, Alert, Link } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, user } = useAuth();
  const navigate = useNavigate();

  // Helper to determine where to redirect after login
  const getRedirectPath = (userData) => {
    // Admin users go directly to dashboard
    if (userData?.systemRole === 'admin' || userData?.systemRole === 'super_admin') {
      return '/dashboard';
    }

    // Check onboarding status
    const onboarding = userData?.onboarding;
    const brands = userData?.brands || [];
    const hasBrandWithCompletePlan = userData?.hasBrandWithCompletePlan;

    // If onboarding is complete OR user has a brand with complete plan
    if (onboarding?.status === 'completed' || hasBrandWithCompletePlan) {
      // Find the first brand with a complete plan and go to its dashboard
      const completeBrand = userData?.brandPlanStatuses?.find(b => b.isComplete);
      if (completeBrand) {
        return `/brands/${completeBrand.brandId}`;
      }
      // Fallback to first brand or dashboard
      if (brands.length > 0) {
        const brandId = brands[0]?.brandId?._id || brands[0]?.brandId;
        return brandId ? `/brands/${brandId}` : '/dashboard';
      }
      return '/dashboard';
    }

    // User needs to complete onboarding
    return '/get-started';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(email, password);
      if (result.success) {
        // Get user data from the login response or context
        // The login function updates the context, but we need the data for redirect
        // We'll use a small delay to ensure context is updated
        setTimeout(() => {
          // Navigate based on onboarding status
          // The ProtectedRoute will also handle this as a fallback
          navigate('/dashboard');
        }, 100);
      } else {
        setError(result.error);
      }
    } catch (error) {
      setError('Login failed. Please try again.');
      console.error('Login failed:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="sm">
      <Box sx={{ marginTop: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Paper elevation={3} sx={{ padding: 4, width: '100%' }}>
          <Typography component="h1" variant="h4" align="center" gutterBottom>
            Studio Sivad Management
          </Typography>
          <Typography variant="h6" align="center" color="text.secondary" gutterBottom>
            Manufacturing Intelligence Platform
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              sx={{ mt: 3, mb: 2 }}
            >
              {loading ? 'Signing In...' : 'Sign In'}
            </Button>

            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Don't have an account?{' '}
                <Link href="/signup" underline="hover" sx={{ cursor: 'pointer' }}>
                  Sign up
                </Link>
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default LoginPage;